import { Flex, Paper, Text } from '@mantine/core';
import {
  IconMoneybag, IconBusinessplan, IconTrendingDown, IconTrophy,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classes from './mortgageInfoBoxes.module.css';

const MortgageInfoBoxes = (props) => {
  const {
    totalsBeforeCalculation,
    effectiveTotalsAfter,
    currentMonthlyPayment,
    mortgageCycleAction,
  } = props;

  const { t } = useTranslation();

  const beforeMonthly = Number(totalsBeforeCalculation?.newMonthlyPayment ?? currentMonthlyPayment);
  const afterMonthly = Number(effectiveTotalsAfter?.newMonthlyPayment ?? 0);
  const monthlySaving = (beforeMonthly - afterMonthly).toFixed(0);

  const beforeTotal = Number(totalsBeforeCalculation?.finalPayment ?? 0);
  const afterTotal = Number(effectiveTotalsAfter?.finalPayment ?? 0);
  const totalSaving = Math.round(beforeTotal - afterTotal);

  const totalSavingPercentage = beforeTotal > 0
    ? Number((((beforeTotal - afterTotal) / beforeTotal) * 100).toFixed(1))
    : 0;

  const getValueColor = (before, after, isLowerBetter = true) => {
    if (before === after) return 'gray';
    const improved = isLowerBetter ? after < before : after > before;
    return improved ? 'var(--mantine-color-green-7)' : 'var(--mantine-color-red-7)';
  };

  const monthlyPaymentColor = getValueColor(beforeMonthly, afterMonthly, true);
  const monthlySavingColor = monthlySaving > 0 ? 'var(--mantine-color-green-7)' : 'var(--mantine-color-red-7)';
  const totalSavingColor = totalSaving > 0 ? 'var(--mantine-color-green-7)' : 'var(--mantine-color-red-7)';
  const returnConversionColor = getValueColor(
    totalsBeforeCalculation?.returnConversion,
    effectiveTotalsAfter?.returnConversion,
    true,
  );

  const boxesInfo = [
    {
      title: t('pages.mortgageCyclesSummary.newMonthlyPayment'),
      value: `${Math.round(afterMonthly).toLocaleString()} ₪`,
      info: t('pages.mortgageCyclesSummary.newMonthlyPaymentInfo'),
      avatar: <IconMoneybag color="#00ABAB" />,
      borderColor: '#00ABAB',
      color: monthlyPaymentColor,
      showBox: true,
    },
    {
      title: t('pages.mortgageCyclesSummary.monthlySaving'),
      value: `${Number(monthlySaving).toLocaleString()} ₪`,
      info: t('pages.mortgageCyclesSummary.monthlySavingInfo'),
      avatar: <IconTrendingDown color="#00ABAB" />,
      borderColor: '#00ABAB',
      color: monthlySavingColor,
      showBox: true,
    },
    {
      title: t('pages.mortgageCyclesSummary.totalSaving'),
      value: `${totalSaving.toLocaleString()} ₪`,
      info: t('pages.mortgageCyclesSummary.totalSavingInfo', { percentage: totalSavingPercentage }),
      avatar: <IconTrophy color="#00ABAB" />,
      borderColor: '#00ABAB',
      color: totalSavingColor,
      showBox: mortgageCycleAction !== 'increasingTheLoanPeriod',
    },
    {
      title: t('pages.mortgageCyclesSummary.returnConversionTitle'),
      value: `${Number(effectiveTotalsAfter?.returnConversion).toFixed(2)} ₪`,
      info: t('pages.mortgageCyclesSummary.returnConversionInfo', { amount: totalsBeforeCalculation?.returnConversion.toFixed(2) }),
      avatar: <IconBusinessplan color="#00ABAB" />,
      borderColor: '#00ABAB',
      color: returnConversionColor,
      showBox: true,
    },
  ];

  return (
    <Flex gap='sm' direction={{ base: 'column', sm: 'row' }}>
      {boxesInfo.map((box, index) => (box.showBox && (
        <Paper
          key={index}
          className={classes.boxInfo}
          style={{ borderTopColor: box.borderColor }}
        >
          <Flex direction='column' justify='center' align='center' gap='xs'>
            {box.avatar}
            <Text size='sm'>{box.title}</Text>
            <Text fw='bold' c={box.color}>{box.value}</Text>
            <Text size='sm'>{box.info}</Text>
          </Flex>
        </Paper>
      )))}
    </Flex>
  );
};

MortgageInfoBoxes.propTypes = {
  totalsBeforeCalculation: PropTypes.shape({
    newMonthlyPayment: PropTypes.number,
    finalPayment: PropTypes.number,
    returnConversion: PropTypes.number,
  }),
  effectiveTotalsAfter: PropTypes.shape({
    newMonthlyPayment: PropTypes.number,
    finalPayment: PropTypes.number,
    returnConversion: PropTypes.number,
  }),
  currentMonthlyPayment: PropTypes.number,
  mortgageCycleAction: PropTypes.string,
};

MortgageInfoBoxes.defaultProps = {
  totalsBeforeCalculation: null,
  effectiveTotalsAfter: null,
  currentMonthlyPayment: 0,
  mortgageCycleAction: '',
};

export default MortgageInfoBoxes;
